import React from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import { NoirBackground, Button } from '../components/GameUI';
import { FileText, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const Home = ({ setPage }) => {
  const { setGameState } = useGame();

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'IMPOSTER', text: 'Play IMPOSTER - a social deduction party game!', url });
        return;
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center p-6 relative select-none">
      <NoirBackground />

      {/* Share button - top right */}
      <motion.button
        onClick={handleShare}
        data-testid="share-btn"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="fixed top-5 right-5 z-30 p-2.5 rounded-full bg-gradient-to-b from-[#1A1A1A] to-[#111] border border-[#222] text-[#5A5A5A] hover:text-[#EF4444] hover:border-[#EF4444]/30 transition-colors"
      >
        <Share2 size={16} />
      </motion.button>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="w-full max-w-md flex flex-col items-center gap-8 relative z-10">

        <motion.div className="text-center w-full" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="font-mono text-[#5A5A5A] text-xs uppercase tracking-[0.4em] mb-4">
            Case File #4781
          </div>
          <h1 className="font-typewriter text-7xl text-[#E0DDD5] uppercase leading-none flicker">
            IMPOSTER
          </h1>
          <div className="red-line w-full mt-5" />
        </motion.div>

        <motion.div className="stamp" initial={{ opacity: 0, rotate: -8 }} animate={{ opacity: 1, rotate: -3 }} transition={{ delay: 0.3 }}>
          Classified
        </motion.div>

        <motion.div className="w-full space-y-3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <Button data-testid="start-game-btn" color="red" onClick={() => setGameState('setup')} className="py-5">
            Open Case
          </Button>
          <button data-testid="how-to-play-btn" onClick={() => setPage('howtoplay')}
            className="btn-noir w-full py-3.5 rounded-lg font-mono text-sm uppercase tracking-wider bg-gradient-to-b from-[#1A1A1A] to-[#111] border border-[#222] text-[#666] flex items-center justify-center gap-2">
            <FileText size={16} /> How to Play
          </button>
        </motion.div>

        <motion.div className="flex items-center justify-center gap-3 text-[10px] font-mono" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
          <a href="https://www.instagram.com/henrykunjumon/" target="_blank" rel="noopener noreferrer" data-testid="footer-henry-link"
            className="text-[#333] hover:text-[#5A5A5A] transition-colors cursor-pointer relative z-30 pointer-events-auto flex items-center gap-1">Henry Kunjumon <span className="text-[#EF4444]">&#9829;</span></a>
          <span className="text-[#222]">|</span>
          <button onClick={() => setPage('privacy')} data-testid="footer-privacy-link"
            className="text-[#333] hover:text-[#5A5A5A] transition-colors cursor-pointer relative z-30">Privacy</button>
          <span className="text-[#222]">|</span>
          <button onClick={() => setPage('terms')} data-testid="footer-terms-link"
            className="text-[#333] hover:text-[#5A5A5A] transition-colors cursor-pointer relative z-30">Terms</button>
          <span className="text-[#222]">|</span>
          <button onClick={() => setPage('contact')} data-testid="footer-contact-link"
            className="text-[#333] hover:text-[#5A5A5A] transition-colors cursor-pointer relative z-30">Contact</button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Home;
